import {
  ChevronLeft,
  ChevronRight,
  Description,
  DirectionsCar,
  ExpandMore,
  PictureAsPdf,
  Timer,
  TrendingUp,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Fade,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { colorPrimary } from "../../../theme";
import { DashboardSalesSintetico, RelatorioFilterValues, SalesSintetico } from "../types/types";

import { useToastStore } from "../../../stores";
import { getFirstDayOfMonth } from "../../../utils/methods/inititalDateTimeFilter";
import { exportSalesSinteticoCsv, exportSalesSinteticoPdf, fetchDashBoardSalesSintetico } from "../repository/AdminRepository";
import { RelatorioFilterBar } from "./RelatorioFilterBar";

/** Backend usa data_final exclusivo (< @data_fim); +1 dia inclui o dia inteiro selecionado no filtro. */
function addOneDayToYmd(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + 1);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2.5rem"
            fontWeight={900}
            letterSpacing={-2}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.75rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={48} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: "#e2e8f0",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function TableRowSkeleton() {
  return (
    <TableRow>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableCell key={i} sx={{ py: 2.2, borderBottom: "1px solid #f8fafc" }}>
          <Skeleton
            variant="text"
            width={i === 3 ? 160 : i === 5 ? 90 : 80}
            height={20}
            sx={{ borderRadius: 1 }}
          />
        </TableCell>
      ))}
    </TableRow>
  );
}

const ITEMS_PER_PAGE = 10;

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR");
}

function formatCurrency(val: number): string {
  return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });
}

function formatMinutesToHM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, "0")}h`;
}

function formatNumber(val: number | null): string {
  if (val == null) return "0";
  return val.toLocaleString("pt-BR");
}

export function SalesSinteticoTab() {
  const [filters, setFilters] = useState<RelatorioFilterValues>({
    grupo: "todos",
    concessionaria: "todos",
    dataInicio: getFirstDayOfMonth(),
    dataFim: new Date().toISOString().split("T")[0] || "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [dashBoard, setDashBoard] = useState<DashboardSalesSintetico | null>(null);
  const [loading, setLoading] = useState(true);
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);
  const vendas: SalesSintetico[] = dashBoard?.vendas ?? [];
  const totalPages = Math.max(1, Math.ceil(vendas.length / ITEMS_PER_PAGE));
  const paginatedVendas = vendas.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const toggleRowExpansion = (idx: number) => {
    setExpandedRows((prev) => (prev.includes(idx) ? prev.filter((r) => r !== idx) : [...prev, idx]));
  };

const handleExportCsv = useCallback(async() => {
    try {
        setLoading(true);
        const result = await exportSalesSinteticoCsv(
          filters.dataInicio, filters.dataFim,
          filters.grupo !== "todos" ? filters.grupo : null,
          filters.concessionaria !== "todos" ? filters.concessionaria : null,
        );
        if (!result?.success) {
            setToast({ variant: "error", message: result.message });
            return;
        }
        setToast({ variant: "success", message: "Relatório exportado com sucesso." });
    } catch (error) {
        setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
        setLoading(false);
    }
},[filters, setToast]);

const handleExportPdf = useCallback(async() => {
    try {
        setLoading(true);
        const result = await exportSalesSinteticoPdf(
          filters.dataInicio, filters.dataFim,
          filters.grupo !== "todos" ? filters.grupo : null,
          filters.concessionaria !== "todos" ? filters.concessionaria : null,
        );
        if (!result?.success) {
            setToast({ variant: "error", message: result.message });
            return;
        }
        setToast({ variant: "success", message: "Relatório exportado com sucesso." });
    } catch (error) {
        setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
        setLoading(false);
    }
},[filters, setToast]);

  const fetchDash = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchDashBoardSalesSintetico({
        id_grupo_concessionaria: filters.grupo !== "todos" ? Number(filters.grupo) : null,
        id_concessionaria: filters.concessionaria !== "todos" ? Number(filters.concessionaria) : null,
        data_inicio: filters.dataInicio || null,
        data_fim: filters.dataFim ? addOneDayToYmd(filters.dataFim) : null,
      });
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setDashBoard(result.data);
      setCurrentPage(1);
    } catch (error) {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [filters, setToast]);

  useEffect(() => {
    if(jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchDash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


const resetFilters = useCallback(  () => {
  setFilters({
    grupo: "todos",
    concessionaria: "todos",
    dataInicio: getFirstDayOfMonth(),
    dataFim: new Date().toISOString().split("T")[0] || "",
  });
   fetchDash();
}, []);



  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={4}>
        {/* FILTROS */}
        <AnimatedBox $delay={0}>
          <RelatorioFilterBar
            values={filters}
            onChange={setFilters}
            onSearch={fetchDash}
            onClear={resetFilters}
            loading={loading}
          />
        </AnimatedBox>

        {/* KPI CARDS */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={80} />
              <KpiCardSkeleton delay={160} />
              <KpiCardSkeleton delay={240} />
              <KpiCardSkeleton delay={320} />
            </>
          ) : (
            <>
              <KpiCard
                label="Total de Vendas"
                value={formatNumber(dashBoard?.total_vendas ?? null)}
                subtitle="vendas realizadas"
                icon={<TrendingUp />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={80}
              />
              <KpiCard
                label="Tempo Médio de Venda"
                value={dashBoard?.tempo_medio_venda_minuto != null ? formatMinutesToHM(dashBoard.tempo_medio_venda_minuto) : "0:00h"}
                subtitle="h:m"
                icon={<Timer />}
                accentColor="#ef4444"
                bgColor="#fef2f2"
                delay={160}
              />
              <KpiCard
                label="Views Totais"
                value={formatNumber(dashBoard?.qtd_views ?? null)}
                subtitle="Visualizações"
                icon={<Visibility />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={240}
              />
              <KpiCard
                label="Veículos Vendidos"
                value={formatNumber(dashBoard?.qtd_veiculo_vendido ?? null)}
                subtitle="Veículos vendidos"
                icon={<DirectionsCar />}
                accentColor={colorPrimary}
                bgColor="#fff7ed"
                delay={320}
              />
            </>
          )}
        </Box>

        {/* TABELA DE VENDAS */}
        <AnimatedBox $delay={400}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid #f1f5f9",
              boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
            }}
          >
            {/* Header da tabela */}
            <Box
              px={3.5}
              py={3}
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <Box>
                <Typography fontWeight={800} fontSize="1.15rem" color="#1e293b">
                  Detalhes de Vendas
                </Typography>
                <Typography fontSize="0.82rem" color="#94a3b8" fontWeight={500}>
                  Listagem detalhada das transações efetuadas no período
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdf sx={{ color: "#ef4444" }} />}
                  sx={{
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: 2,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#ef4444",
                      bgcolor: "#fef2f2",
                    },
                  }}
                  onClick={handleExportPdf}
                  disabled={loading}
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Description sx={{ color: "#10b981" }} />}
                  onClick={handleExportCsv}
                  disabled={loading}
                  sx={{
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: 2,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#10b981",
                      bgcolor: "#ecfdf5",
                    },
                  }}
                >
                  CSV
                </Button>
              </Box>
            </Box>

            {/* Tabela */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    {[
                      "Data Cadastro",
                      "Anunciante",
                      "Comprador",
                      "Veículo",
                      "Data Venda",
                      "Valor",
                      "Views",
                      "Tempo",
                    ].map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 1.5,
                          color: "#94a3b8",
                          borderBottom: "2px solid #f1f5f9",
                          py: 2,
                          whiteSpace: "nowrap",
                          ...(col === "Views" && { textAlign: "center" }),
                          ...(col === "Tempo" && { textAlign: "right" }),
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} />)
                  ) : paginatedVendas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 6, textAlign: "center" }}>
                        <Typography fontSize="0.9rem" color="#94a3b8" fontWeight={500}>
                          Nenhum resultado encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : paginatedVendas.map((row, idx) => {
                    const isCombo = row.veiculo.length > 1;
                    const firstVehicle = row.veiculo[0];
                    return (
                      <React.Fragment key={row.id_anuncio}>
                        <TableRow
                          sx={{
                            transition: "all 0.2s",
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: colorPrimary + "08",
                            },
                            "& td": {
                              borderBottom: "1px solid #f8fafc",
                            },
                          }}
                        >
                          <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2 }}>
                            {formatDate(row.dat_cadastro)}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#1e293b", py: 2.2 }}>
                            {row.concessionaria}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.85rem", color: "#64748b", py: 2.2 }}>
                            {row.revenda}
                          </TableCell>
                          <TableCell sx={{ py: 2.2 }}>
                            <Box display="flex" alignItems="flex-start" gap={1.5}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 26,
                                  borderRadius: 1,
                                  bgcolor: "#f1f5f9",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  position: "relative",
                                  overflow: "hidden",
                                }}
                              >
                                <DirectionsCar sx={{ fontSize: 16, color: "#94a3b8" }} />
                                {isCombo && (
                                  <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    display="grid"
                                    gridTemplateColumns="1fr 1fr"
                                    gap="1px"
                                    sx={{ opacity: 0.15 }}
                                  >
                                    <Box bgcolor="#94a3b8" />
                                    <Box bgcolor="#64748b" />
                                  </Box>
                                )}
                              </Box>
                              <Box flex={1}>
                                <Stack direction="row" alignItems="center" gap={1} mb={0.25}>
                                  {isCombo && (
                                    <Chip
                                      label="COMBO"
                                      size="small"
                                      sx={{
                                        bgcolor: colorPrimary,
                                        color: "white",
                                        fontSize: "0.5rem",
                                        fontWeight: 900,
                                        height: 18,
                                        textTransform: "uppercase",
                                        fontStyle: "italic",
                                        "& .MuiChip-label": { px: 0.75 },
                                      }}
                                    />
                                  )}
                                  <Typography fontSize="0.85rem" fontWeight={700} color="#1e293b" noWrap>
                                    {`${firstVehicle?.veiculo ?? ""}${isCombo ? ` + ${row.veiculo.length - 1}` : ""}`}
                                  </Typography>
                                </Stack>
                                {isCombo ? (
                                  <>
                                    <Typography fontSize="0.6875rem" color="#94a3b8" mb={0.5}>
                                      {row.veiculo.length} Veículos no pacote
                                    </Typography>
                                    <Box
                                      display="inline-flex"
                                      alignItems="center"
                                      gap={0.5}
                                      sx={{ cursor: "pointer" }}
                                      onClick={() => toggleRowExpansion(idx)}
                                    >
                                      <Typography
                                        fontSize="0.625rem"
                                        fontWeight={700}
                                        color={colorPrimary}
                                        textTransform="uppercase"
                                        letterSpacing={0.5}
                                        sx={{ "&:hover": { color: "#ea580c" } }}
                                      >
                                        Detalhes
                                      </Typography>
                                      <ExpandMore
                                        sx={{
                                          fontSize: 14,
                                          color: colorPrimary,
                                          transform: expandedRows.includes(idx) ? "rotate(180deg)" : "rotate(0deg)",
                                          transition: "transform 0.3s",
                                        }}
                                      />
                                    </Box>
                                  </>
                                ) : (
                                  <Typography fontSize="0.6875rem" color="#94a3b8" fontWeight={500}>
                                    {firstVehicle?.placa}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#10b981", py: 2.2 }}
                          >
                            {formatDate(row.dat_venda)}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "0.85rem", fontWeight: 900, color: "#1e293b", py: 2.2, whiteSpace: "nowrap" }}
                          >
                            {formatCurrency(row.val_venda)}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#0284c7", textAlign: "center", py: 2.2 }}
                          >
                            {formatNumber(row.qtd_total_click)}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#64748b", textAlign: "right", py: 2.2 }}
                          >
                            {formatMinutesToHM(row.time_sell)}
                          </TableCell>
                        </TableRow>

                        {isCombo && (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              sx={{
                                py: 0,
                                borderBottom: "1px solid #f1f5f9",
                                bgcolor: idx % 2 === 1 ? "rgba(248, 250, 252, 0.5)" : "transparent",
                              }}
                            >
                              <Collapse in={expandedRows.includes(idx)} timeout="auto" unmountOnExit>
                                <Box py={2} pl={4} pr={4}>
                                  <Stack spacing={1} borderLeft={`2px solid ${colorPrimary}33`} pl={2}>
                                    {row.veiculo.map((v) => (
                                      <Box
                                        key={v.placa}
                                        bgcolor="white"
                                        p={1.5}
                                        borderRadius={1.5}
                                        border="1px solid #f1f5f9"
                                        sx={{ boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
                                      >
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          <Typography fontSize="0.825rem" fontWeight={700}>
                                            {v.veiculo}
                                          </Typography>
                                          <Chip
                                            label={v.placa}
                                            size="small"
                                            sx={{
                                              bgcolor: "#f1f5f9",
                                              color: "#475569",
                                              fontWeight: 700,
                                              fontSize: "0.6875rem",
                                              letterSpacing: 1,
                                            }}
                                          />
                                        </Stack>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              px={3.5}
              py={2.5}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderTop="1px solid #f1f5f9"
              bgcolor="#fafbfc"
            >
              <Typography fontSize="0.82rem" color="#94a3b8" fontWeight={500}>
                Mostrando {paginatedVendas.length} de {vendas.length} resultados
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  sx={{ color: "#94a3b8" }}
                >
                  <ChevronLeft />
                </IconButton>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <Box
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        ...(currentPage === page
                          ? {
                              bgcolor: colorPrimary,
                              color: "white",
                              boxShadow: `0 2px 8px ${colorPrimary}40`,
                            }
                          : {
                              color: "#64748b",
                              "&:hover": { bgcolor: "#f1f5f9" },
                            }),
                      }}
                    >
                      {page}
                    </Box>
                  );
                })}
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  sx={{ color: "#94a3b8" }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </AnimatedBox>
      </Box>
    </Fade>
  );
}
